import { zodResolver } from '@hookform/resolvers/zod';
import { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  FileMimeTypes,
  FilePicker,
  ItemStatus,
  S3FileUploader,
} from '@/modules/common';

import { maxFilePreviewSize } from '../constants';
import { MaskUploadUrlData } from '../types';
import { MaskFormSchema } from '../validations';

type MaskFormValues = z.infer<typeof MaskFormSchema>;

interface MaskFormProps {
  editMode?: boolean;
  uploadUrlData?: MaskUploadUrlData | null;
  defaultValues?: MaskFormValues;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: MaskFormValues) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
}

const defaultFormValues = {
  name: '',
  status: '',
  largeUrl: '',
  smallUrl: '',
  transparentUrl: '',
  updatedAt: undefined,
};

export function MaskForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
  uploadUrlData,
  refetchUploadCredentials,
}: MaskFormProps) {
  const form = useForm<MaskFormValues>({
    resolver: zodResolver(MaskFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async () => {
    await onSubmit(form.getValues());
  };

  const filesConfig = [
    {
      key: 'largeUrl',
      uploadUrl: uploadUrlData?.imagesUploadUrls?.large,
      fileUrl: defaultValues?.largeUrl,
    },
    {
      key: 'smallUrl',
      uploadUrl: uploadUrlData?.imagesUploadUrls?.small,
      fileUrl: defaultValues?.smallUrl,
    },
    {
      key: 'transparentUrl',
      uploadUrl: uploadUrlData?.imagesUploadUrls?.transparent,
      fileUrl: defaultValues?.transparentUrl,
    },
  ];

  return (
    <Form {...form}>
      <S3FileUploader
        config={filesConfig}
        onComplete={handleFormSubmit}
        refetchCredentials={refetchUploadCredentials}
      >
        {({
          retryUpload,
          filesState,
          isUploading,
          onFileChange,
          uploadFiles,
        }) => (
          <form onSubmit={form.handleSubmit(uploadFiles)}>
            {children({ isLoading: isUploading })}

            <div className="flex flex-col gap-10 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mask name</FormLabel>
                      <FormControl>
                        <Input placeholder="Add mask name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="updatedAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last modified</FormLabel>
                      <FormControl>
                        <Input
                          disabled
                          placeholder="YYYY-MM-DD"
                          required={false}
                          readOnly
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ItemStatus
                  name="status"
                  isDisabled={!editMode}
                  control={form.control}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                <FilePicker
                  control={form.control}
                  label="Big size mask"
                  name="largeUrl"
                  withFilePreview
                  accept={[FileMimeTypes.Png]}
                  onRetry={retryUpload}
                  isUploading={isUploading}
                  file={filesState.largeUrl?.file}
                  error={filesState.largeUrl?.error}
                  onFileChange={onFileChange}
                  placeholder="Add mask file"
                  tips={
                    <p className="text-sm text-zinc-500">
                      The file must be in
                      <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                      format and maximum size 10 MB.
                    </p>
                  }
                  maxFileSize={maxFilePreviewSize}
                />

                <FilePicker
                  control={form.control}
                  label="Small size mask"
                  name="smallUrl"
                  withFilePreview
                  accept={[FileMimeTypes.Png]}
                  onRetry={retryUpload}
                  isUploading={isUploading}
                  file={filesState.smallUrl?.file}
                  error={filesState.smallUrl?.error}
                  onFileChange={onFileChange}
                  placeholder="Add mask file"
                  tips={
                    <p className="text-sm text-zinc-500">
                      The file must be in
                      <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                      format and maximum size 10 MB.
                    </p>
                  }
                  maxFileSize={maxFilePreviewSize}
                />

                <FilePicker
                  control={form.control}
                  label="No background mask"
                  name="transparentUrl"
                  withFilePreview
                  accept={[FileMimeTypes.Png]}
                  onRetry={retryUpload}
                  isUploading={isUploading}
                  file={filesState.transparentUrl?.file}
                  error={filesState.transparentUrl?.error}
                  onFileChange={onFileChange}
                  placeholder="Add mask file"
                  tips={
                    <p className="text-sm text-zinc-500">
                      The file must be in
                      <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                      format and maximum size 10 MB.
                    </p>
                  }
                  maxFileSize={maxFilePreviewSize}
                />
              </div>
            </div>
          </form>
        )}
      </S3FileUploader>
    </Form>
  );
}
